import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface CartItem {
  product_id: number
  slug: string
  name: string
  kind?: string          // product kind (e.g. 'domain') — gates kind-specific cart UI
  currency: string
  billing_cycle: string
  quantity: number
  config: Record<string, unknown>
  base_price: number   // price for the selected cycle, before options
  setup_fee: number
  total_price: number  // base + option modifiers, pre-coupon
}

interface CartState {
  items: CartItem[]
  coupon: string
  addItem: (item: CartItem) => void
  removeItem: (slug: string) => void
  updateQuantity: (slug: string, qty: number) => void
  updateItemConfig: (slug: string, config: Record<string, unknown>) => void
  setCoupon: (code: string) => void
  clear: () => void
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      items: [],
      coupon: '',

      addItem: (item) =>
        set((s) => {
          // Same product+cycle → replace (updated config/qty)
          const existing = s.items.findIndex(
            (i) => i.slug === item.slug && i.billing_cycle === item.billing_cycle,
          )
          if (existing >= 0) {
            const updated = [...s.items]
            updated[existing] = item
            return { items: updated }
          }
          return { items: [...s.items, item] }
        }),

      removeItem: (slug) =>
        set((s) => ({ items: s.items.filter((i) => i.slug !== slug) })),

      updateQuantity: (slug, qty) =>
        set((s) => ({
          items: s.items.map((i) =>
            i.slug === slug ? { ...i, quantity: Math.max(1, qty) } : i,
          ),
        })),

      updateItemConfig: (slug, config) =>
        set((s) => ({
          items: s.items.map((i) =>
            i.slug === slug ? { ...i, config } : i,
          ),
        })),

      setCoupon: (code) => set({ coupon: code }),

      clear: () => set({ items: [], coupon: '' }),
    }),
    { name: 'nc-cart' },
  ),
)
